import React from 'react'
import { Box, IconButton, useMediaQuery } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import TabsMui from '../../../../layout/TabsMui'
import { layoutCompactMediaQuery } from '../../../../layout/layoutConstants'
import { UI_TEXT_STATUS } from '../../../../constants/uiText'
import { UI_COLOR_PRIMARY_ALT } from '../../../../constants/uiColors'
import { REFUND_APPROVAL_TEXT } from '../../../constants/refundApprovalText'

const RefundApprovalTabsHeader = ({
  tabs,
  activeTab,
  onChange,
  statusOption,
  activity,
  compact = false
}) => {
  const statusLabel = statusOption?.label || activity?.status || '-'
  const shouldUseSingleTab = useMediaQuery(layoutCompactMediaQuery, { noSsr: true })
  const activeTabIndex = React.useMemo(() => {
    const index = tabs.findIndex((tab) => tab.value === activeTab)
    return index >= 0 ? index : 0
  }, [activeTab, tabs])
  const shouldPaginateCompactTabs = compact && shouldUseSingleTab && tabs.length > 1
  const canGoToPreviousTab = activeTabIndex > 0
  const canGoToNextTab = activeTabIndex < tabs.length - 1
  const visibleTabs = shouldPaginateCompactTabs
    ? tabs.slice(activeTabIndex, activeTabIndex + 1)
    : tabs

  const handleCompactTabNavigation = React.useCallback(
    (nextIndex) => {
      const nextTab = tabs[nextIndex] || tabs[0]
      if (!nextTab) return

      onChange(nextTab.value)
    },
    [onChange, tabs]
  )

  return (
    <Box
      className="show-area-tabs-header"
      sx={{
        display: compact ? 'grid' : 'flex',
        gridTemplateColumns: compact ? 'minmax(0, 1fr) auto' : undefined,
        alignItems: 'center',
        justifyContent: 'space-between',
        columnGap: compact ? 1 : 2,
        rowGap: compact ? 0.75 : 2,
        flexWrap: compact ? undefined : { xs: 'wrap', md: 'nowrap' },
        borderBottom: 1,
        borderColor: 'divider',
        mb: compact ? 1.25 : 2,
        ...(compact
          ? {
              '& .MuiTab-root': {
                minHeight: 44,
                minWidth: 0,
                px: { xs: 1, sm: 1.5 },
                fontSize: { xs: 13, sm: 14 }
              }
            }
          : {})
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          flex: compact ? undefined : 1
        }}
      >
        {shouldPaginateCompactTabs && canGoToPreviousTab && (
          <IconButton
            type="button"
            aria-label={REFUND_APPROVAL_TEXT.showPreviousTab}
            title={REFUND_APPROVAL_TEXT.showPreviousTab}
            size="small"
            onClick={() => handleCompactTabNavigation(activeTabIndex - 1)}
            sx={{
              flex: '0 0 auto',
              width: 32,
              height: 32,
              mr: 0.25,
              color: UI_COLOR_PRIMARY_ALT
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}

        <Box
          sx={{
            minWidth: 0,
            flex: shouldPaginateCompactTabs ? '0 1 auto' : 1
          }}
        >
          <TabsMui
            tabs={visibleTabs}
            activeTab={activeTab}
            onChange={onChange}
            showDivider={false}
            marginBottom={0}
            activeColor={UI_COLOR_PRIMARY_ALT}
            scrollable={false}
          />
        </Box>

        {shouldPaginateCompactTabs && canGoToNextTab && (
          <IconButton
            type="button"
            aria-label={REFUND_APPROVAL_TEXT.showNextTab}
            title={REFUND_APPROVAL_TEXT.showNextTab}
            size="small"
            onClick={() => handleCompactTabNavigation(activeTabIndex + 1)}
            sx={{
              flex: '0 0 auto',
              width: 32,
              height: 32,
              ml: 0.25,
              color: UI_COLOR_PRIMARY_ALT
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box
        className="status-orders"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: compact ? 'flex-end' : 'flex-start',
          minWidth: 0,
          mb: compact ? 0 : { xs: 1, md: 0 },
          '& .order-status-b': {
            mt: 1,
            whiteSpace: 'nowrap'
          },
          '& .order-status': {
            whiteSpace: 'nowrap',
            ...(compact
              ? {
                  width: 'auto',
                  minWidth: 116,
                  maxWidth: { xs: '100%', sm: '36vw' },
                  height: 26,
                  px: 1.25,
                  fontSize: 12,
                  lineHeight: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              : {
                  width: 120,
                  minWidth: 120
                })
          }
        }}
      >
        {!compact && <b className="order-status-b">{UI_TEXT_STATUS}</b>}
        <span className={statusOption?.className || 'order-status'}>{statusLabel}</span>
      </Box>
    </Box>
  )
}

export default RefundApprovalTabsHeader
